import * as React from 'react';
import { DayPicker, useDayPicker, type CalendarMonth } from 'react-day-picker';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const NAV_BTN_CLS =
  'h-7 w-7 inline-flex items-center justify-center rounded-md ' +
  'text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors ' +
  'disabled:opacity-30 disabled:pointer-events-none';

function CalendarMonthCaption({ calendarMonth }: { calendarMonth: CalendarMonth }) {
  const { goToMonth, previousMonth, nextMonth, startMonth, endMonth } = useDayPicker() as ReturnType<typeof useDayPicker> & { startMonth?: Date; endMonth?: Date };
  const date  = calendarMonth.date;
  const year  = date.getFullYear();
  const month = date.getMonth();

  const fromYear = startMonth ? startMonth.getFullYear() : 1950;
  const toYear   = endMonth   ? endMonth.getFullYear()   : new Date().getFullYear() + 10;
  const years    = Array.from({ length: toYear - fromYear + 1 }, (_, i) => fromYear + i);

  return (
    <div className="flex items-center justify-between h-9 w-full">
      <button
        type="button"
        aria-label="Previous month"
        disabled={!previousMonth}
        onClick={() => previousMonth && goToMonth(previousMonth)}
        className={NAV_BTN_CLS}
      >
        <ChevronLeft className="h-4 w-4" />
      </button>

      <div className="flex items-center gap-1">
        <Select
          value={String(month)}
          onValueChange={val => {
            const d = new Date(date);
            d.setMonth(Number(val));
            goToMonth(d);
          }}
        >
          <SelectTrigger className="h-7 w-auto gap-1 border-none bg-transparent px-2 text-sm font-semibold shadow-none hover:bg-accent focus:ring-0 focus:ring-offset-0 [&>svg]:opacity-60">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {MONTH_NAMES.map((name, i) => (
              <SelectItem key={i} value={String(i)}>{name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={String(year)}
          onValueChange={val => {
            const d = new Date(date);
            d.setFullYear(Number(val));
            goToMonth(d);
          }}
        >
          <SelectTrigger className="h-7 w-auto gap-1 border-none bg-transparent px-2 text-sm font-semibold shadow-none hover:bg-accent focus:ring-0 focus:ring-offset-0 [&>svg]:opacity-60">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="max-h-56">
            {years.map(y => (
              <SelectItem key={y} value={String(y)}>{y}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <button
        type="button"
        aria-label="Next month"
        disabled={!nextMonth}
        onClick={() => nextMonth && goToMonth(nextMonth)}
        className={NAV_BTN_CLS}
      >
        <ChevronRight className="h-4 w-4" />
      </button>
    </div>
  );
}

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

export function Calendar({ className, classNames, showOutsideDays = true, ...props }: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn('p-3', className)}
      classNames={{
        months: 'flex flex-col sm:flex-row gap-4',
        month: 'flex flex-col gap-4',
        month_caption: 'flex h-9',
        caption_label: 'text-sm font-semibold',
        nav: 'hidden',
        month_grid: 'w-full border-collapse',
        weekdays: 'flex',
        weekday: 'text-muted-foreground rounded-md w-9 font-normal text-[0.8rem] text-center',
        week: 'flex w-full mt-2',
        day: 'relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected].day-range-end)]:rounded-r-md',
        day_button: cn(
          'h-9 w-9 p-0 font-normal rounded-md inline-flex items-center justify-center text-sm',
          'hover:bg-accent hover:text-accent-foreground transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          'aria-selected:opacity-100',
        ),
        range_start: 'day-range-start [&>button]:bg-primary [&>button]:text-primary-foreground [&>button]:hover:bg-primary [&>button]:hover:text-primary-foreground rounded-l-md',
        range_end: 'day-range-end [&>button]:bg-primary [&>button]:text-primary-foreground [&>button]:hover:bg-primary [&>button]:hover:text-primary-foreground rounded-r-md',
        selected: '[&>button]:bg-primary [&>button]:text-primary-foreground [&>button]:hover:bg-primary [&>button]:hover:text-primary-foreground',
        today: '[&>button]:bg-accent [&>button]:text-accent-foreground',
        outside: 'day-outside text-muted-foreground aria-selected:bg-accent/50 aria-selected:text-muted-foreground',
        disabled: 'text-muted-foreground opacity-50',
        range_middle: 'aria-selected:bg-accent aria-selected:text-accent-foreground',
        hidden: 'invisible',
        ...classNames,
      }}
      components={{
        MonthCaption: CalendarMonthCaption,
      }}
      {...props}
    />
  );
}
Calendar.displayName = 'Calendar';
